import Image from "next/image";

import type { ExperienceHighlight, FocusTrack } from "./content";
import { cn } from "@/utils";
import styles from "@/ui/styles/shared.module.css";

type ExperienceIntroProps = {
	highlights: ExperienceHighlight[];
	tracks: FocusTrack[];
};

export function ExperienceIntro({ highlights, tracks }: ExperienceIntroProps) {
	return (
		<div className={cn(
			"rounded-[24px] sm:rounded-[32px] border border-white/10 bg-black/30 px-4 sm:px-6 md:px-8 py-6 sm:py-8 md:py-10 shadow-[0_25px_80px_rgba(5,7,19,0.65)] backdrop-blur-xl max-w-6xl mx-auto",
			styles.commonShell
		)}>
			<div>
				<p className="sectionSubText">Flight path</p>
				<h2 className="sectionHeadText">Experience</h2>
			</div>
			<p className="paragraphText mt-4 max-w-3xl text-white/70 text-sm sm:text-base md:text-lg">
				A sampling of the crews, labs, and brands I've helped—from education to
				commerce to immersive storytelling. Each stop blends strategy, design,
				and dev to get ambitious missions out the door.
			</p>
			<div className="mt-6 sm:mt-8 grid gap-4 sm:gap-6 lg:grid-cols-[1.2fr_0.8fr]">
				<div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2">
					{highlights.map((highlight) => (
						<div
							key={highlight.title}
							className="rounded-xl sm:rounded-2xl border border-white/10 bg-white/5 p-3 sm:p-4"
						>
							<p className="text-[10px] sm:text-xs uppercase tracking-[0.35em] text-white/40">
								{highlight.title}
							</p>
							<p className="mt-2 text-xs sm:text-sm text-white/80">{highlight.body}</p>
						</div>
					))}
				</div>
				<div className="rounded-xl sm:rounded-2xl border border-white/10 bg-black/30 p-3 sm:p-4">
					<p className="text-[10px] sm:text-xs uppercase tracking-[0.35em] text-white/40">
						Focus lanes
					</p>
					<div className="mt-3 sm:mt-4 flex flex-wrap gap-2 sm:gap-3">
						{tracks.map((track) => (
							<span
								key={track.name}
								className="flex items-center gap-1.5 sm:gap-2 rounded-full border border-white/10 bg-white/5 px-2 sm:px-3 py-1 text-[10px] sm:text-xs uppercase tracking-[0.3em] text-white/70"
							>
								<div className="relative h-3 w-3 sm:h-4 sm:w-4 overflow-hidden">
									<Image
										src={track.icon}
										alt={track.name}
										fill
										sizes="(max-width: 640px) 12px, 16px"
										className="object-contain"
									/>
								</div>
								{track.name}
							</span>
						))}
					</div>
				</div>
			</div>
		</div>
	);
}

